<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { computed, reactive, ref, watch } from 'vue'
import {
  confirmSellerBankAccountAction,
  sellerRejectTransactionAction,
} from '@/api/functions/transactions/transaction-status.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  transaction: { type: Object },
  sellerIranianSrc: { type: Array },
  sellerIranian: { type: Array },
  sellerRevolut: { type: Array },
  sellerPaypal: { type: Array },
  sellerInternational: { type: Array },
  sellerWise: { type: Array },
})

const emit = defineEmits(['accept', 'reject', 'createAccount'])

const toast = useToast()
const queryClient = useQueryClient()

const form = reactive({
  sellerBank: null,
})

watch(
  () => [props.transaction],
  ([transaction]) => {
    if (transaction) {
      form.sellerBank = transaction.bankAccounts?.sellerBank?.id || null
    }
  },
  { immediate: true }
)

// const isOwner = computed(() => props.transaction?.offer.owner.id === props.transaction?.seller.id)

// const isDisabled = () => isOwner.value ? props.transaction?.offerOwnerAcceptance : props.transaction?.requisitionOwnerAcceptance;
const isDisabled = computed(() => {
  if (props.transaction?.requisition?.type === 'BUY') {
    return props.transaction?.offerOwnerAcceptance
  }

  return props.transaction?.requisitionOwnerAcceptance
})

const sellerAcceptMutation = useMutationApi({
  mutationFn: async () => {
    const uuid = props.transaction?.uuid
    if (!uuid) throw new Error('شناسه تراکنش یافت نشد')

    return confirmSellerBankAccountAction(uuid, {
      iranian_bank_account_id: form.sellerBank,
    })
  },

  onSuccess: () => {
    toast.success('حساب فروشنده با موفقیت تایید شد')
    emit('accept')
    queryClient.invalidateQueries({ queryKey: ['transactionDetails', props.transaction?.uuid] })
  },

  onError: (err: unknown) => {
    toast.error(getErrorMessage(err) || 'خطا در تایید حساب فروشنده')
  },
})

const sellerRejectMutation = useMutationApi({
  mutationFn: async () => {
    const uuid = props.transaction?.uuid
    if (!uuid) throw new Error('شناسه تراکنش یافت نشد')

    return sellerRejectTransactionAction(uuid)
  },

  onSuccess: () => {
    toast.success('تراکنش توسط فروشنده رد شد')
    emit('reject')
    queryClient.invalidateQueries({ queryKey: ['transactionDetails', props.transaction?.uuid] })
  },

  onError: (err: unknown) => {
    toast.error(getErrorMessage(err) || 'خطا در رد تراکنش توسط فروشنده')
  },
})
</script>

<template>
  <div>
    <div
      class="mb-2.5 flex items-center justify-center text-[#2d5396] text-[15px] rounded-xs font-medium bg-[#fffbcf] h-[79px]">
      فروشنده
    </div>

    <div v-if="transaction?.bankAccounts?.sellerBank">
      <span class="text-[#2d5396] text-[15px] ms-3">حساب مقصد ریالی</span>
      <div class="flex flex-col lg:grid lg:grid-cols-12 items-center lg:items-start mt-10 gap-6 lg:px-6">
        <Select v-model="form.sellerBank" class="col-span-7" label="حساب مقصد ریالی" :options="sellerIranian"
          :disabled="isDisabled" />

        <Button :isAdmin="true" @click="() => emit('createAccount', 'مقصد ریالی', null, false)" type="button"
          :disabled="isDisabled"
          class=" bg-transparent rounded-xl  cursor-pointer hover:bg-[#d7deeb] text-[#2d5396]! font-medium col-span-5 h-[46px]">
          <span class="flex items-center justify-center gap-2  text-nowrap">
            <img src="/svg/circle-plus.svg" />
            شماره حساب جدید
          </span>
        </Button>
      </div>
    </div>

    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-8 lg:gap-0 mt-4">
      <div class="flex items-center text-[#2d5396] text-[15px] font-medium gap-6">
        <span class="">وضعیت تایید:</span>
        <div class="flex items-center"
          :class="isDisabled ? 'text-[#4caf50]' : 'text-[#f44336]'">
          <img width="16" height="16"
            :src="isDisabled ? '/svg/check-circle-svgrepo-com.svg' : '/svg/info-circle-svgrepo-com.svg'" />
          {{ isDisabled ? 'تایید شده' : 'هنوز تایید نشده' }}
        </div>
      </div>

      <div class="w-full lg:w-fit flex items-center justify-end lg:justify-start gap-1 mb-4">
        <Button @click="sellerAcceptMutation.mutate()" type="button" :disabled="isDisabled" label="تایید "
          :isAdmin="true" class="w-fit px-3!  h-8! rounded-lg! font-medium! text-[13px]!" />
        <Button @click="sellerRejectMutation.mutate()" :disabled="isDisabled" type="button"
          label="رد " variant="outline" :isAdmin="true"
          class="w-fit px-3!  h-8! rounded-lg! border-2 font-medium! text-[13px]!" />
      </div>
    </div>
  </div>
</template>
